import { useState } from 'react'
import { Menu, Bell, LogOut, Key, ChevronDown } from 'lucide-react'
import ChangePasswordModal from './ChangePasswordModal'
import { theme, T, ensureFonts } from './report-shell/tokens'

const Header = ({ onMenuClick, user, onLogout }) => {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)

  ensureFonts()

  const getUserInitials = () => {
    if (!user?.name) return 'U'
    return user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
  }

  const roleColors = {
    ADMIN:   { bg: '#f3e8ff', fg: '#6b21a8' },
    MANAGER: { bg: '#dbeafe', fg: '#1e40af' },
    KAM:     { bg: '#dcfce7', fg: '#166534' },
  }
  const rc = roleColors[user?.role] || { bg: theme.bgSub, fg: theme.textSub }

  return (
    <header style={{
      height: 56,
      flexShrink: 0,
      background: theme.surface,
      borderBottom: `1px solid ${theme.border}`,
      display: 'flex', alignItems: 'center',
      padding: '0 24px', gap: 16,
      fontFamily: T.fontUI,
    }}>
      <button
        onClick={onMenuClick}
        className="lg:hidden"
        style={{
          padding: 6, borderRadius: 6, border: 'none', background: 'transparent',
          color: theme.textDim, cursor: 'pointer',
        }}
      ><Menu size={18} /></button>

      <div style={{ flex: 1 }} />

      {/* Live indicator */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, color: theme.textSub,
        padding: '5px 10px', borderRadius: 6,
        background: theme.bgSub, border: `1px solid ${theme.border}`,
        fontFamily: T.fontMono,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: theme.good,
          boxShadow: `0 0 0 2px ${theme.good}22`,
          animation: 'pulse 2s infinite',
        }} />
        Live
      </div>

      {/* Bell */}
      <button style={{
        width: 32, height: 32, borderRadius: 7,
        background: theme.bgSub, border: `1px solid ${theme.border}`,
        color: theme.textSub, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
      }}>
        <Bell size={15} />
      </button>

      {/* User menu */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setShowUserMenu(v => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '5px 10px 5px 5px', borderRadius: 8,
            background: 'transparent', border: '1px solid transparent',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = theme.bgSub }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.accent}, ${theme.text})`,
            color: '#fff', fontWeight: 600, fontSize: 11,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            letterSpacing: -0.3,
          }}>{getUserInitials()}</div>
          <div className="hidden md:flex" style={{
            flexDirection: 'column', alignItems: 'flex-start',
            lineHeight: 1.15, minWidth: 0,
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: theme.text, whiteSpace: 'nowrap' }}>
              {user?.name || 'User'}
            </span>
            <span style={{ fontSize: 10.5, color: theme.textDim, whiteSpace: 'nowrap' }}>
              {user?.email}
            </span>
          </div>
          <ChevronDown size={14} style={{ color: theme.textDim }} />
        </button>

        {showUserMenu && (
          <>
            <div onClick={() => setShowUserMenu(false)} style={{
              position: 'fixed', inset: 0, zIndex: 10,
            }} />
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 6px)',
              width: 264, zIndex: 20,
              background: theme.surface, borderRadius: 10,
              border: `1px solid ${theme.border}`,
              boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            }}>
              <div style={{ padding: 14, borderBottom: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.accent}, ${theme.text})`,
                    color: '#fff', fontWeight: 700, fontSize: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{getUserInitials()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.name}
                    </div>
                    <div style={{ fontSize: 11, color: theme.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.email}
                    </div>
                  </div>
                </div>
                {user?.role && (
                  <span style={{
                    display: 'inline-block', marginTop: 8,
                    padding: '2px 8px', borderRadius: 999,
                    background: rc.bg, color: rc.fg,
                    fontSize: 10.5, fontWeight: 600, letterSpacing: 0.2,
                  }}>{user.role}</span>
                )}
              </div>
              <div style={{ padding: 4 }}>
                <button onClick={() => { setShowUserMenu(false); setShowChangePassword(true) }}
                  style={menuItemStyle}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.bgSub}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Key size={14} style={{ color: theme.textDim }} />
                  <span style={{ flex: 1, textAlign: 'left' }}>Zmień hasło</span>
                </button>
                {onLogout && (
                  <button onClick={() => { setShowUserMenu(false); onLogout() }}
                    style={{ ...menuItemStyle, color: theme.bad }}
                    onMouseEnter={(e) => e.currentTarget.style.background = `${theme.bad}11`}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={14} />
                    <span style={{ flex: 1, textAlign: 'left' }}>Wyloguj</span>
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onPasswordChanged={() => {}}
      />
    </header>
  )
}

const menuItemStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '8px 10px', borderRadius: 6,
  border: 'none', background: 'transparent',
  color: theme.text, fontSize: 12.5, fontWeight: 500,
  cursor: 'pointer', fontFamily: T.fontUI,
}

export default Header
